import React, { useEffect, useState } from 'react'
import { X, Loader2, Check, AlertTriangle } from 'lucide-react'
import { fetchCategories } from '../../api'
import type { Category } from '../../types'

interface PublishModalProps {
  title: string
  description: string
  onConfirm: (data: { title: string; description: string; categoryIds: number[] }) => void
  onCancel: () => void
  publishing: boolean
  initialCategoryIds?: number[]
}

export default function PublishModal({
  title: initialTitle,
  description: initialDescription,
  onConfirm,
  onCancel,
  publishing,
  initialCategoryIds = [],
}: PublishModalProps) {
  const [categories, setCategories] = useState<Category[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>(initialCategoryIds)
  const [loading, setLoading] = useState(true)
  const [title, setTitle] = useState(initialTitle)
  const [description, setDescription] = useState(initialDescription)

  useEffect(() => {
    fetchCategories()
      .then(setCategories)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const toggleCategory = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 3 ? [...prev, id] : prev
    )
  }

  const missingTitle = !title.trim()
  const missingDescription = !description.trim()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900">Publish Demo</h2>
          <button onClick={onCancel} className="p-1 text-slate-400 hover:text-slate-600 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-slate-600 mb-5">
          Review your demo details before making it public.
        </p>

        {/* Title */}
        <div className="mb-4">
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 block">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Give your demo a title"
            className={`w-full bg-slate-50 border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent outline-none transition-all ${
              missingTitle ? 'border-red-300' : 'border-slate-200'
            }`}
          />
          {missingTitle && (
            <p className="text-xs text-red-500 mt-1">A title is required to publish.</p>
          )}
        </div>

        {/* Description */}
        <div className="mb-4">
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 block">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what your demo does — this appears in search results and social previews"
            rows={3}
            className={`w-full bg-slate-50 border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent outline-none transition-all resize-none ${
              missingDescription ? 'border-amber-300' : 'border-slate-200'
            }`}
          />
          {missingDescription && (
            <div className="flex items-start gap-1.5 mt-1">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-600">
                Adding a description improves SEO and social sharing previews.
              </p>
            </div>
          )}
        </div>

        {/* Category chips */}
        <div className="mb-5">
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2 block">
            Categories {selectedIds.length > 0 && `(${selectedIds.length}/3)`}
          </label>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading...
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => {
                const selected = selectedIds.includes(cat.id)
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleCategory(cat.id)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                      selected
                        ? 'border-[#5048e5] bg-[#5048e5]/10 text-[#5048e5]'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                    } ${!selected && selectedIds.length >= 3 ? 'opacity-40 cursor-not-allowed' : ''}`}
                    disabled={!selected && selectedIds.length >= 3}
                  >
                    {selected && <Check className="h-3 w-3" />}
                    {cat.name}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm({ title: title.trim(), description: description.trim(), categoryIds: selectedIds })}
            disabled={publishing || missingTitle}
            className="flex items-center gap-2 bg-[#5048e5] text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm shadow-[#5048e5]/25 hover:bg-[#5048e5]/90 transition-all disabled:opacity-60"
          >
            {publishing && <Loader2 className="h-4 w-4 animate-spin" />}
            {publishing ? 'Publishing...' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  )
}
